import React, { useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { formatarData } from '@spark/utils/formatters';
import { depositoNotaFiscalAnexoFilterConfig } from '../config/depositoNotaFiscalAnexoFilterConfig';
import { useDepositoNotaFiscalAnexos } from '../hooks';

const DepositoNotaFiscalAnexosModal = ({ show, onHide, notaId, numeroNF, reloadTrigger }) => {
  const {
    anexos,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = useDepositoNotaFiscalAnexos(show ? notaId : null);

  useEffect(() => {
    if (show && reloadTrigger > 0) {
      reload();
    }
  }, [reloadTrigger, show, reload]);

  const rows = useMemo(
    () => (Array.isArray(anexos) ? anexos : []),
    [anexos]
  );

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: 'Id',
        accessorKey: 'id',
        cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
      },
      {
        id: 'dataCriacao',
        header: 'Data Criacao',
        accessorKey: 'dataCriacao',
        cell: ({ getValue }) => (getValue() ? formatarData(getValue()) : '-')
      },
      {
        id: 'usuarioCriacao',
        header: 'Usuario',
        accessorKey: 'usuarioCriacao',
        cell: ({ getValue }) => <span>{getValue() || '-'}</span>
      },
      {
        id: 'urlAnexo',
        header: 'Anexo',
        accessorKey: 'urlAnexo',
        cell: ({ getValue }) => {
          const url = getValue();
          if (!url) return '-';
          return (
            <a href={url} target="_blank" rel="noreferrer">
              <TruncatedText value={url} maxLength={30} />
            </a>
          );
        }
      }
    ],
    []
  );

  const modalTitle = numeroNF ? `Anexos - NF ${numeroNF}` : 'Anexos';

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!notaId ? (
          <div className="text-muted">Selecione uma nota fiscal para visualizar anexos.</div>
        ) : (
          <>
            <FilterPanel
              filters={depositoNotaFiscalAnexoFilterConfig}
              onApply={handleFiltrosChange}
              title="Filtros de Anexos"
              loading={loading}
              defaultCollapsed={false}
            />

            <TanStackTable
              key={`deposito-anexos-${paginaAtual}-${quantidadePorPagina}`}
              columns={columns}
              data={rows}
              loading={loading}
              withPagination={true}
              manualPagination={true}
              totalItems={totalRegistros}
              controlledPagination={{
                pageIndex: Math.max(0, (paginaAtual || 1) - 1),
                pageSize: quantidadePorPagina || 10
              }}
              onPaginationChange={handlePaginationChange}
              pageSizeOptions={[5, 10, 25, 50]}
              enableRowColors={true}
              className="mt-2"
            />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DepositoNotaFiscalAnexosModal;
